import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart,
    faUser,
    faPhone,
    faMapMarkerAlt,
    faCalendarAlt,
    faFileInvoice,
    faBox,
    faRupeeSign,
    faTag,
    faBalanceScale,
    faDollarSign,
    faCalculator,
    faExclamationTriangle,
    faPlus,
    faMinus,
    faTrash,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationCircle,
    faSearch,
    faBarcode,
    faQrcode,
    faCreditCard,
    faMoneyBill,
    faUniversity,
    faWallet,
    faPercent,
    faReceipt,
    faChevronDown,
    faChevronUp,
    faBoxes,
    faHistory,
    faUndo,
    faRedo,
    faPrint,
    faFilePdf,
    faDownload,
    faUpload,
    faEdit,
    faChartLine,
    faTimesCircle,
    faShoppingBag,
    faAddressCard,
    faIdCard,
    faKey,
    faLock,
    faUnlock,
    faEye,
    faEyeSlash,
    faStore,
    faEnvelope,
    faGlobe,
    faBuilding
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility
// ==============================================
const createSellTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_sell_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Sell Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_sell_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        newSale: 'New Sale',
        editSale: 'Edit Sale',
        saleDetails: 'Sale Details',
        sellProducts: 'Sell Products',
        billNumber: 'Bill Number',
        date: 'Date',
        shop: 'Shop',
        customerDetails: 'Customer Details',
        customerName: 'Customer Name',
        customerPhone: 'Phone Number',
        customerAddress: 'Address',
        searchCustomer: 'Search customer...',
        addNewCustomer: 'Add New Customer',
        existingCustomer: 'Existing Customer',
        newCustomer: 'New Customer',
        customerLoginCreated: 'Customer login created',
        customerUsername: 'Username',
        customerPassword: 'Password',
        customerInfo: 'Customer Information',
        searchProducts: 'Search Products',
        searchPlaceholder: 'Search by name, batch or barcode...',
        scanBarcode: 'Scan Barcode',
        selectProduct: 'Select Product',
        noProductsFound: 'No products found',
        outOfStock: 'Out of Stock',
        lowStock: 'Low Stock',
        availableStock: 'Available',
        items: 'Items',
        addItem: 'Add Item',
        productName: 'Product',
        batchNumber: 'Batch',
        unit: 'Unit',
        price: 'Price',
        quantity: 'Qty',
        total: 'Total',
        remove: 'Remove',
        stock: 'Stock',
        calculations: 'Calculations',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        paymentMethod: 'Payment Method',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        credit: 'Credit',
        other: 'Other',
        paymentStatus: 'Payment Status',
        paid: 'Paid',
        partial: 'Partial',
        unpaid: 'Unpaid',
        completeSale: 'Complete Sale',
        updateSale: 'Update Sale',
        clear: 'Clear',
        saving: 'Saving...',
        loading: 'Loading...',
        saleCompleted: 'Sale completed successfully!',
        saleUpdated: 'Sale updated successfully!',
        failedSave: 'Failed to save sale',
        failedUpdate: 'Failed to update sale',
        networkError: 'Network error occurred',
        duplicateSubmission: 'Duplicate submission detected. Please wait.',
        noShopSelected: 'No shop selected. Please select a shop first.',
        customerSelected: 'Customer selected',
        customerCreated: 'New customer created successfully',
        customerLoginInfo: 'Customer login: {username} / welcome',
        itemsRequired: 'At least one item is required',
        quantityExceedsStock: 'Quantity exceeds available stock',
        invalidQuantity: 'Please enter a valid quantity',
        invalidPrice: 'Please enter a valid price',
        piece: 'Piece',
        box: 'Box',
        packet: 'Packet',
        kg: 'Kilogram',
        gram: 'Gram',
        liter: 'Liter',
        ml: 'Milliliter',
        dozen: 'Dozen',
        selectUnit: 'Select unit',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        todaySales: "Today's Sales",
        totalSales: 'Total Sales',
        totalAmount: 'Total Amount',
        totalPaid: 'Total Paid',
        totalBalance: 'Total Balance',
        notes: 'Notes',
        printReceipt: 'Print Receipt',
        receipt: 'RECEIPT',
        paymentDetails: 'Payment Details',
        thankYou: 'Thank you for your business!',
        footer: 'C auto year ourzest pvt ltd',
        printerSettings: 'Printer Settings',
        selectPrinter: 'Select Printer',
        paperSize: 'Paper Size',
        thermalPrinter: 'Thermal Printer',
        standardPrinter: 'Standard Printer',
        copies: 'Copies',
        businessInfo: 'Business Information',
        invoice: 'INVOICE',
        gst: 'GST',
        cashier: 'Cashier',
        transactionId: 'Transaction ID',
        poweredBy: 'Powered by OurZest',
        itemsSold: 'Items Sold',
        printing: 'Printing receipt...',
        printSuccess: 'Receipt sent to printer',
        close: 'Close',
        print: 'Print'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const sellTranslator = createSellTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationCircle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations?.success || 'Success';
            case 'error':
                return translations?.error || 'Error';
            case 'warning':
                return translations?.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Items Table Component
// ==============================================
const ItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    errors,
    disabled,
    translations,
    isRTL
}) => {
    const tableRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const calculateTotal = (price, quantity) => {
        const p = parseFloat(price) || 0;
        const q = parseFloat(quantity) || 0;
        return (p * q).toFixed(2);
    };

    const units = [
        { value: 'piece', label: translations?.piece || 'Piece' },
        { value: 'box', label: translations?.box || 'Box' },
        { value: 'packet', label: translations?.packet || 'Packet' },
        { value: 'kg', label: translations?.kg || 'Kilogram' },
        { value: 'gram', label: translations?.gram || 'Gram' },
        { value: 'liter', label: translations?.liter || 'Liter' },
        { value: 'ml', label: translations?.ml || 'Milliliter' },
        { value: 'dabba', label: translations?.dabba || 'Dabba' },
        { value: 'burai', label: translations?.burai || 'Burai' },
        { value: 'dozen', label: translations?.dozen || 'Dozen' }
    ];

    if (isMobile) {
        return (
            <div className="items-mobile-container">
                <div className="items-header-mobile">
                    <h4>
                        <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                        {translations?.items || 'Items'}
                    </h4>
                    <button
                        type="button"
                        className="btn-add-item-mobile"
                        onClick={onAdd}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="items-cards-container">
                    {items.map((item, index) => (
                        <div key={index} className="item-card-mobile">
                            <div className="item-card-header">
                                <span className="item-number">
                                    <FontAwesomeIcon icon={faBox} className="header-icon-only" />
                                    Item {index + 1}
                                </span>
                                <button
                                    type="button"
                                    className="item-remove-btn-mobile"
                                    onClick={() => onRemove(index)}
                                    disabled={disabled || items.length === 1}
                                >
                                    <FontAwesomeIcon icon={faTrash} />
                                </button>
                            </div>

                            <div className="item-card-content">
                                <div className={`mobile-field ${errors[`product_name_${index}`] ? 'has-error' : ''}`}>
                                    <label>
                                        <FontAwesomeIcon icon={faBox} className="field-icon" />
                                        {translations?.productName || 'Product'}
                                    </label>
                                    <input
                                        type="text"
                                        value={item.product_name}
                                        readOnly
                                        className="readonly-input"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                </div>

                                <div className="mobile-field">
                                    <label>{translations?.batchNumber || 'Batch'}</label>
                                    <input
                                        type="text"
                                        value={item.batch_no}
                                        readOnly
                                        className="readonly-input"
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                </div>

                                <div className="mobile-field stock-info">
                                    <label>{translations?.availableStock || 'Available'}</label>
                                    <div className={`stock-badge ${parseFloat(item.available_stock) <= parseFloat(item.quantity_alert) ? 'low-stock' : ''}`}>
                                        {parseFloat(item.available_stock || item.quantity || 0).toFixed(2)} {item.unit}
                                    </div>
                                </div>

                                <div className="mobile-row">
                                    <div className={`mobile-field half ${errors[`unit_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.unit || 'Unit'} *</label>
                                        <select
                                            value={item.unit}
                                            onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            <option value="">{translations?.selectUnit || 'Select'}</option>
                                            {units.map((unit) => (
                                                <option key={unit.value} value={unit.value}>
                                                    {unit.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className={`mobile-field half ${errors[`price_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.price || 'Price'} *</label>
                                        <input
                                            type="number"
                                            value={item.price}
                                            onChange={(e) => onItemChange(index, 'price', e.target.value)}
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            disabled={disabled}
                                            dir="ltr"
                                        />
                                    </div>
                                </div>

                                <div className="mobile-row">
                                    <div className={`mobile-field half ${errors[`quantity_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.quantity || 'Qty'} *</label>
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                                            step="0.001"
                                            min="0.001"
                                            max={item.available_stock}
                                            placeholder="0.000"
                                            disabled={disabled}
                                            dir="ltr"
                                        />
                                    </div>

                                    <div className="mobile-field half">
                                        <label>{translations?.total || 'Total'}</label>
                                        <input
                                            type="text"
                                            value={calculateTotal(item.price, item.quantity)}
                                            readOnly
                                            disabled
                                            className="readonly-input total-display"
                                        />
                                    </div>
                                </div>

                                {errors[`quantity_${index}`] && (
                                    <div className="error-text">
                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                        {errors[`quantity_${index}`]}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4>
                    <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                    {translations?.items || 'Items'}
                </h4>
                <button
                    type="button"
                    className="btn-add-item-table"
                    onClick={onAdd}
                    disabled={disabled}
                >
                    <FontAwesomeIcon icon={faPlus} />
                    <span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="table-wrapper" ref={tableRef}>
                <table className="items-table">
                    <thead>
                        <tr>
                            <th className="product-col">
                                {translations?.productName || 'Product'} <span className="required">*</span>
                            </th>
                            <th className="batch-col">
                                {translations?.batchNumber || 'Batch'}
                            </th>
                            <th className="unit-col">
                                {translations?.unit || 'Unit'} <span className="required">*</span>
                            </th>
                            <th className="price-col">
                                {translations?.price || 'Price'} <span className="required">*</span>
                            </th>
                            <th className="qty-col">
                                {translations?.quantity || 'Qty'} <span className="required">*</span>
                            </th>
                            <th className="total-col">
                                {translations?.total || 'Total'}
                            </th>
                            <th className="action-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={index} className={errors[`quantity_${index}`] ? 'error-row' : ''}>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={item.product_name}
                                            readOnly
                                            className="readonly-input"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {/* {item.barcode && (
                                            <small className="barcode-display">
                                                <FontAwesomeIcon icon={faBarcode} size="xs" />
                                                {item.barcode}
                                            </small>
                                        )} */}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={item.batch_no}
                                            readOnly
                                            className="readonly-input"
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <select
                                            value={item.unit}
                                            onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                            className={errors[`unit_${index}`] ? 'error' : ''}
                                        >
                                            <option value="">{translations?.selectUnit || 'Select'}</option>
                                            {units.map((unit) => (
                                                <option key={unit.value} value={unit.value}>
                                                    {unit.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="number"
                                            value={item.price}
                                            onChange={(e) => onItemChange(index, 'price', e.target.value)}
                                            step="0.01"
                                            min="0"
                                            placeholder="0.00"
                                            disabled={disabled}
                                            dir="ltr"
                                            className={errors[`price_${index}`] ? 'error' : ''}
                                        />
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                                            step="0.001"
                                            min="0.001"
                                            max={item.available_stock}
                                            placeholder="0.000"
                                            disabled={disabled}
                                            dir="ltr"
                                            className={errors[`quantity_${index}`] ? 'error' : ''}
                                        />
                                        <small className="stock-hint">
                                            {translations?.stock || 'Stock'}: {parseFloat(item.available_stock || item.quantity || 0).toFixed(2)} {item.unit}
                                        </small>
                                        {errors[`quantity_${index}`] && (
                                            <div className="table-error">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {errors[`quantity_${index}`]}
                                            </div>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <div className="table-field">
                                        <input
                                            type="text"
                                            value={calculateTotal(item.price, item.quantity)}
                                            readOnly
                                            disabled
                                            className="readonly-input total-display"
                                        />
                                    </div>
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        className="table-remove-btn"
                                        onClick={() => onRemove(index)}
                                        disabled={disabled || items.length === 1}
                                        title={translations?.remove || 'Remove'}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ==============================================
// Customer Login Info Modal Component
// ==============================================
const CustomerLoginModal = ({ show, customer, onClose, translations, isRTL }) => {
    const [showPassword, setShowPassword] = useState(false);

    if (!show || !customer) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content customer-login-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>
                        <FontAwesomeIcon icon={faIdCard} />
                        {translations?.customerLoginCreated || 'Customer Login Created'}
                    </h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="customer-info-badge">
                        <FontAwesomeIcon icon={faCheckCircle} className="success-icon" />
                        <span className="customer-name">{customer.name}</span>
                        <span className="customer-status">{translations?.newCustomer || 'New Customer'}</span>
                    </div>

                    <div className="login-credentials">
                        <h4>{translations?.customerInfo || 'Customer Information'}</h4>

                        <div className="credential-item">
                            <div className="credential-label">
                                <FontAwesomeIcon icon={faUser} />
                                {translations?.customerUsername || 'Username'}:
                            </div>
                            <div className="credential-value">
                                <code>{customer.customer_username}</code>
                            </div>
                        </div>

                        <div className="credential-item">
                            <div className="credential-label">
                                <FontAwesomeIcon icon={faLock} />
                                {translations?.customerPassword || 'Password'}:
                            </div>
                            <div className="credential-value password-value">
                                <code>
                                    {showPassword ? 'welcome' : '••••••••••••'}
                                </code>
                                <button
                                    type="button"
                                    className="toggle-password"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                </button>
                            </div>
                        </div>

                        <div className="credential-note">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            <span>Please save these credentials. The customer can use them to login.</span>
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button className="btn-primary" onClick={onClose}>
                        {translations?.okay || 'OK'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// MODERN RECEIPT MODAL COMPONENT
// ==============================================
// In sale.js, replace the entire ReceiptModal component with this:

// Complete replacement for ReceiptModal in sale.js

// Complete replacement for ReceiptModal in sale.js

const ReceiptModal = ({ show, sale, businessInfo, shopInfo, translations, onPrint, onClose }) => {
    const receiptRef = useRef(null);

    // Owner details - Customize these
    const ownerName = "منیر خان";
    const mobileNumbers = ["03150559988", "03158000062"];

    // Complete product name translations
    const productTranslations = {
        "mix packet wala": "مکس پیکٹ والا",
        "mixpc": "مکس پیکٹ",
        "super karlez(25 packet)": "سپر کرلیز (25 پیکٹ)",
        "super karlez": "سپر کرلیز",
        "spk01": "سپر کرلیز",
        "salanty(20 packet)": "سلنٹی (20 پیکٹ)",
        "slt11": "سلنٹی",
        "salanty": "سلنٹی",
        "mix 25 bandal wala": "مکس 25 بندل والا",
        "mix": "مکس",
        "used bore": "یوزڈ بور",
        "ub01": "یوزڈ بور",
        "white biryani (24 pack)": "وائٹ بریانی (24 پیک)",
        "wht01": "وائٹ بریانی",
        "white biryani": "وائٹ بریانی",
        "SUPER BIRYANI RAFER(KG)": "سپر بریانی ریفر",
        "SBR": "سپر بریانی ریفر",
        "super biryani rafer": "سپر بریانی ریفر",
        "white biryani rafer(kg)": "وائٹ بریانی ریفر",
        "wr01": "وائٹ بریانی ریفر",
        "white biryani rafer": "وائٹ بریانی ریفر",
        "wbr01": "وائٹ بریانی ریفر",
        "SUPER BIRYAN (25)BANDAL": "سپر بریانی (25 بندل)",
        "SB01": "سپر بریانی",
        "super biryan": "سپر بریانی",
        "SALANTY PLASTIC(22\\36)BANDAL WALA": "سلنٹی پلاسٹک (22/36) بندل",
        "STP2": "سلنٹی پلاسٹک",
        "salanty plastic": "سلنٹی پلاسٹک",
        "MIX W/CH/SA PLASTIC SIZE(10\\16)PACKING WALA": "مکس پلاسٹک (10/16)",
        "MIXS2": "مکس پلاسٹک",
        "mix plastic small": "مکس پلاسٹک چھوٹا",
        "MIX W/CH/SA PLASTIC SIZE(18\\30)BNDAL W": "مکس پلاسٹک (18/30)",
        "MIX1": "مکس پلاسٹک بڑا",
        "mix plastic large": "مکس پلاسٹک بڑا",
        "KHAN G MASALA(KG)": "خان جی مصالحہ",
        "KGM1": "خان جی مصالحہ",
        "khan g masala": "خان جی مصالحہ",
        "SUPER BIRYANI MASALA(KG)": "سپر بریانی مصالحہ",
        "SB1": "سپر بریانی مصالحہ",
        "super biryani masala": "سپر بریانی مصالحہ",
        "SALANTY MASALA(KG)": "سلنٹی مصالحہ",
        "SM1": "سلنٹی مصالحہ",
        "salanty masala": "سلنٹی مصالحہ",
        "SUPER KARLEZ MASLA(KG)": "سپر کرلیز مصالحہ",
        "SKM": "سپر کرلیز مصالحہ",
        "super karlez masala": "سپر کرلیز مصالحہ",
        "CHARSI MASALA(KG)": "چرسی مصالحہ",
        "CM1": "چرسی مصالحہ",
        "charsi masala": "چرسی مصالحہ",
        "WHITE MASLALA": "وائٹ مصالحہ",
        "WS1": "وائٹ مصالحہ",
        "white masala": "وائٹ مصالحہ",
        "mix shafer": "مکس شیفر",
        "mix01": "مکس شیفر",
        "wood": "لکڑی",
        "wd01": "لکڑی",
        "khali bori": "خالی بوری",
        "kb01": "خالی بوری",
        "SALANTY (10\\14)SIZE PLASTIC": "سلنٹی پلاسٹک (10/14)",
        "pl2": "سلنٹی پلاسٹک چھوٹا",
        "gee": "گھی",
        "g01": "گھی",
        "maza party(25 bandal)": "مزا پارٹی (25 بندل)",
        "mp01": "مزا پارٹی",
        "maza party": "مزا پارٹی",
        "khan g (25 bandal)": "خان جی (25 بندل)",
        "kg01": "خان جی",
        "khan g": "خان جی",
        "khan g rafer(kg)": "خان جی ریفر",
        "kgr01": "خان جی ریفر",
        "khan g rafer": "خان جی ریفر",
        "s karlz rafer(kg)": "ایس کرلز ریفر",
        "skr01": "ایس کرلز ریفر",
        "s karlz rafer": "ایس کرلز ریفر",
        "kacha fafer": "کچا فیفر",
        "kf01": "کچا فیفر",
        "salanty rafer(kg)": "سلنٹی ریفر",
        "slr01": "سلنٹی ریفر",
        "salanty rafer": "سلنٹی ریفر",
        "charsi biryani rafer(kg)": "چرسی بریانی ریفر",
        "cr01": "چرسی بریانی ریفر",
        "charsi biryani rafer": "چرسی بریانی ریفر",
        "maza party rafer(kg)": "مزا پارٹی ریفر",
        "mr01": "مزا پارٹی ریفر",
        "maza party rafer": "مزا پارٹی ریفر",
        "S BIRYANI RAFER(kilogram)": "ایس بریانی ریفر",
        "sr01": "ایس بریانی ریفر",
        "s biryani rafer": "ایس بریانی ریفر",
        "Packet": "پیکٹ",
        "burai": "بوری",
        "Kilogram": "کلوگرام",
        "Box": "بکس",
        "Piece": "ٹکڑا",
        "ڈبہ": "ڈبہ"
    };

    const translateProductName = (productName) => {
        if (!productName) return '';
        const lowerName = productName.toLowerCase();

        if (productTranslations[productName]) return productTranslations[productName];

        for (const [english, urdu] of Object.entries(productTranslations)) {
            if (lowerName.includes(english.toLowerCase())) {
                return urdu;
            }
        }

        return productName;
    };

    // Improved date formatter that handles various date formats
    const formatDate = (dateString) => {
        if (!dateString) {
            // Return today's date if no date provided
            const today = new Date();
            const day = today.getDate().toString().padStart(2, '0');
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const year = today.getFullYear();
            return `${day}/${month}/${year}`;
        }

        try {
            // Try to parse the date
            let date = new Date(dateString);

            // If invalid, try to parse as YYYY-MM-DD string
            if (isNaN(date.getTime()) && typeof dateString === 'string') {
                // Check if it's in YYYY-MM-DD format
                const parts = dateString.split('-');
                if (parts.length === 3) {
                    date = new Date(parts[0], parts[1] - 1, parts[2]);
                }
            }

            // If still invalid, return today's date
            if (isNaN(date.getTime())) {
                const today = new Date();
                const day = today.getDate().toString().padStart(2, '0');
                const month = (today.getMonth() + 1).toString().padStart(2, '0');
                const year = today.getFullYear();
                return `${day}/${month}/${year}`;
            }

            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (error) {
            // Return today's date on error
            const today = new Date();
            const day = today.getDate().toString().padStart(2, '0');
            const month = (today.getMonth() + 1).toString().padStart(2, '0');
            const year = today.getFullYear();
            return `${day}/${month}/${year}`;
        }
    };

    const formatTime = () => {
        const now = new Date();
        return now.toLocaleTimeString('en-PK', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const handlePrint = () => {
        if (receiptRef.current) {
            const printContent = receiptRef.current.innerHTML;
            const printWindow = window.open('', '_blank');
            if (printWindow) {
                printWindow.document.write('<html><head><title>رسید</title>');
                printWindow.document.write('<meta charset="UTF-8">');
                printWindow.document.write(`
                    <style>
                        @import url('https://fonts.googleapis.com/css2?family=Noto+Nastaliq+Urdu&display=swap');
                        * {
                            margin: 0;
                            padding: 0;
                            box-sizing: border-box;
                        }
                        @media print {
                            @page {
                                size: A5;
                                margin: 5mm;
                            }
                            body {
                                margin: 0;
                                padding: 0;
                                background: white;
                            }
                        }
                        body {
                            font-family: 'Noto Nastaliq Urdu', 'Courier New', monospace;
                            background: white;
                            direction: rtl;
                            padding: 0;
                            margin: 0;
                        }
                        .receipt-print {
                            width: 100%;
                            max-width: 140mm;
                            margin: 0 auto;
                            padding: 3mm;
                            font-family: 'Noto Nastaliq Urdu', 'Courier New', monospace;
                            font-size: 9pt;
                            direction: rtl;
                            text-align: right;
                        }
                        .text-center { text-align: center; }
                        .text-right { text-align: right; }
                        .font-bold { font-weight: bold; }
                        .border-bottom { border-bottom: 1px solid #000; }
                        .border-top { border-top: 1px solid #000; }
                        .mb-05 { margin-bottom: 1mm; }
                        .mt-05 { margin-top: 1mm; }
                        .pt-05 { padding-top: 0.5mm; }
                        .pb-05 { padding-bottom: 0.5mm; }
                        table { 
                            width: 100%; 
                            border-collapse: collapse;
                            margin: 0.5mm 0;
                        }
                        th { 
                            text-align: center; 
                            border-bottom: 1px solid #000;
                            padding: 1px;
                            font-size: 8pt;
                        }
                        td { 
                            padding: 1px;
                            text-align: center;
                            font-size: 8pt;
                        }
                        td:first-child {
                            text-align: right;
                        }
                        .shop-name {
                            font-size: 12pt;
                            font-weight: bold;
                        }
                        .owner-name {
                            font-size: 9pt;
                            font-weight: bold;
                            color: #0066cc;
                            margin-top: 0;
                            padding-top: 0;
                        }
                        .invoice-title {
                            font-size: 11pt;
                            font-weight: bold;
                            margin: 1mm 0;
                        }
                        .mobile-numbers {
                            font-size: 8pt;
                            font-weight: bold;
                            color: #0066cc;
                            direction: ltr;
                        }
                        .info-row {
                            display: flex;
                            justify-content: space-between;
                            font-size: 8pt;
                            margin: 0.5mm 0;
                        }
                        .customer-row {
                            display: flex;
                            justify-content: space-between;
                            font-size: 8pt;
                            padding: 0.5mm 0;
                            border-top: 1px dashed #000;
                            border-bottom: 1px dashed #000;
                            margin: 0.5mm 0;
                        }
                        .totals-wrapper {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            width: 100%;
                            margin: 1mm 0;
                        }
                        .totals-labels {
                            text-align: right;
                        }
                        .totals-amounts {
                            text-align: left;
                        }
                        .totals-labels div, .totals-amounts div {
                            padding: 1px 0;
                            font-size: 8pt;
                        }
                        .grand-total-label, .grand-total-amount {
                            font-size: 10pt;
                            font-weight: bold;
                            border-top: 2px solid #000;
                            margin-top: 1mm;
                            padding-top: 1mm;
                        }
                        .footer {
                            text-align: center;
                            font-size: 7pt;
                            margin-top: 2mm;
                            padding-top: 1mm;
                            border-top: 1px dashed #000;
                        }
                        @media print {
                            body { margin: 0; padding: 0; }
                        }
                    </style>
                `);
                printWindow.document.write('</head><body>');
                printWindow.document.write('<div class="receipt-print">');
                printWindow.document.write(printContent);
                printWindow.document.write('</div>');
                printWindow.document.write('</body></html>');
                printWindow.document.close();

                setTimeout(() => {
                    printWindow.print();
                    printWindow.onafterprint = () => {
                        printWindow.close();
                    };
                }, 300);
            }
        }
        if (onPrint) onPrint();
    };

    if (!show || !sale) return null;

    const shopName = shopInfo?.name || businessInfo?.businessname || 'ایس ٹی فوڈ کمپنی';
    const shopAddress = businessInfo?.businessaddress || '';
    const shopPhone = businessInfo?.phonenumber || '';

    const subtotal = sale?.subtotal ||
        sale?.items?.reduce((sum, item) => sum + (parseFloat(item.price) * parseFloat(item.quantity)), 0).toFixed(2) || '0.00';
    const tax = sale?.tax || '0.00';
    const discount = sale?.discount || '0.00';
    const grandTotal = sale?.grand_total || (parseFloat(subtotal) + parseFloat(tax) - parseFloat(discount)).toFixed(2);
    const paidAmount = sale?.paid_amount || grandTotal;
    const balance = sale?.balance || (parseFloat(grandTotal) - parseFloat(paidAmount)).toFixed(2);

    // Determine payment status text
    let paymentStatusText = '';
    let paymentStatusColor = '#059669';
    if (parseFloat(balance) <= 0) {
        paymentStatusText = 'ادا';
        paymentStatusColor = '#059669';
    } else if (parseFloat(paidAmount) > 0) {
        paymentStatusText = 'ادھار';
        paymentStatusColor = '#d97706';
    } else {
        paymentStatusText = 'ادھار';
        paymentStatusColor = '#dc2626';
    }

    const urduLabels = {
        invoice: 'رسید فروخت',
        billNo: 'بل نمبر',
        date: 'تاریخ',
        time: 'وقت',
        cashier: 'کیشیر',
        product: 'جنس',
        qty: 'مقدار',
        price: 'قیمت',
        total: 'کل',
        grandTotal: 'کل رقم',
        paid: 'اداگی',
        balance: 'باقی',
        thankYou: 'شکریہ'
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content receipt-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
                <div className="modal-header">
                    <h3>
                        <FontAwesomeIcon icon={faReceipt} />
                        {urduLabels.invoice}
                    </h3>
                    <button className="modal-close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    <div ref={receiptRef} className="receipt-preview" style={{
                        fontFamily: "'Noto Nastaliq Urdu', 'Courier New', monospace",
                        fontSize: '9pt',
                        padding: '8px',
                        background: '#ffffff',
                        direction: 'rtl',
                        textAlign: 'right'
                    }}>
                        {/* Header - Reduced gap between business name and owner name */}
                        <div className="text-center">
                            <div className="shop-name">{shopName}</div>
                            {shopAddress && <div style={{ fontSize: '7pt' }}>{shopAddress}</div>}
                            {shopPhone && <div style={{ fontSize: '7pt' }}>ٹیلی فون: {shopPhone}</div>}
                            <div className="owner-name">{ownerName}</div>
                        </div>

                        <div className="text-center mobile-numbers mb-05">
                            📞 {mobileNumbers.join('  |  📞 ')}
                        </div>

                        <div className="text-center invoice-title">
                            {urduLabels.invoice}
                        </div>

                        {/* Bill Number and Cashier in one row */}
                        <div className="info-row">
                            <span><strong>{urduLabels.billNo}:</strong> #{sale?.bill_number || 'N/A'}</span>
                            <span><strong>{urduLabels.cashier}:</strong> {sale?.username || 'اسٹاف'}</span>
                        </div>

                        {/* Date and Time in one row - FIXED: Now properly displays date */}
                        <div className="info-row mb-05">
                            <span><strong>{urduLabels.date}:</strong> {formatDate(sale?.date || sale?.created_at || sale?.sale_date)}</span>
                            <span><strong>{urduLabels.time}:</strong> {formatTime()}</span>
                        </div>

                        {/* Customer Info - Name and Phone in one row */}
                        {(sale?.customer_name || sale?.customer_phone) && (
                            <div className="customer-row">
                                <span><strong>{sale?.customer_name || ''}</strong></span>
                                <span>{sale?.customer_phone ? `📞 ${sale.customer_phone}` : ''}</span>
                            </div>
                        )}

                        <table>
                            <thead>
                                <tr>
                                    <th style={{ width: '45%' }}>{urduLabels.product}</th>
                                    <th style={{ width: '15%' }}>{urduLabels.qty}</th>
                                    <th style={{ width: '20%' }}>{urduLabels.price}</th>
                                    <th style={{ width: '20%' }}>{urduLabels.total}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sale?.items?.map((item, index) => (
                                    <tr key={index}>
                                        <td style={{ textAlign: 'right' }}>
                                            {translateProductName(item.product_name || item.productName)}
                                        </td>
                                        <td>{parseFloat(item.quantity).toFixed(2)}</td>
                                        <td>{parseFloat(item.price).toFixed(0)}</td>
                                        <td>{parseFloat(item.total || item.price * item.quantity).toFixed(0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Payment Section - Right aligned labels, left aligned amounts */}
                        <div className="border-top pt-05 mt-05">
                            <div className="totals-wrapper">
                                <div className="totals-labels">
                                    {parseFloat(tax) > 0 && <div>{urduLabels.tax || 'ٹیکس'}</div>}
                                    {parseFloat(discount) > 0 && <div>{urduLabels.discount || 'چھوٹ'}</div>}
                                    <div className="grand-total-label">{urduLabels.grandTotal}</div>
                                    <div>{urduLabels.paid}</div>
                                    <div className="grand-total-label" style={{ borderTop: 'none', marginTop: 0, paddingTop: 0 }}>{urduLabels.balance}</div>
                                </div>
                                <div className="totals-amounts">
                                    {parseFloat(tax) > 0 && <div>{parseFloat(tax).toFixed(2)}</div>}
                                    {parseFloat(discount) > 0 && <div>-{parseFloat(discount).toFixed(2)}</div>}
                                    <div className="grand-total-amount">{parseFloat(grandTotal).toFixed(2)}</div>
                                    <div>{parseFloat(paidAmount).toFixed(2)}</div>
                                    <div className="grand-total-amount" style={{ color: parseFloat(balance) > 0 ? '#d97706' : '#059669', borderTop: 'none', marginTop: 0, paddingTop: 0 }}>
                                        {parseFloat(balance).toFixed(2)}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Payment Status - Clean without extra labels */}
                        <div className="text-center" style={{ fontSize: '8pt', margin: '1mm 0', padding: '0.5mm' }}>
                            {urduLabels.paid}: <span style={{ fontWeight: 'bold', color: paymentStatusColor }}>{paymentStatusText}</span>
                        </div>

                        {/* Footer */}
                        <div className="footer">
                            <div style={{ fontWeight: 'bold' }}>🌟 {urduLabels.thankYou} 🌟</div>
                        </div>
                    </div>
                </div>

                <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '12px', borderTop: '1px solid #eee' }}>
                    <button className="btn-secondary" onClick={onClose} style={{ padding: '6px 12px' }}>
                        <FontAwesomeIcon icon={faTimes} style={{ marginRight: '5px' }} />
                        بند کریں
                    </button>
                    <button className="btn-primary" onClick={handlePrint} style={{ padding: '6px 12px', background: 'var(--purchase-primary-color)', color: 'white', border: 'none', borderRadius: '4px' }}>
                        <FontAwesomeIcon icon={faPrint} style={{ marginRight: '5px' }} />
                        پرنٹ کریں
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Business Info Component
// ==============================================
const BusinessInfo = ({ businessInfo, translations }) => {
    if (!businessInfo) return null;

    return (
        <div className="business-info-card" style={{
            background: 'linear-gradient(135deg, #f8fafc, #f1f5f9)',
            borderRadius: '12px',
            padding: '15px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '15px'
        }}>
            {businessInfo.bussinesslogo && (
                <img
                    src={businessInfo.bussinesslogo}
                    alt="Logo"
                    style={{
                        width: '50px',
                        height: '50px',
                        objectFit: 'contain',
                        borderRadius: '8px'
                    }}
                />
            )}
            <div className="business-details">
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>
                    {businessInfo.businessname}
                </div>
                {businessInfo.businessaddress && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faMapMarkerAlt} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.businessaddress}
                    </div>
                )}
                {businessInfo.phonenumber && (
                    <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                        <FontAwesomeIcon icon={faPhone} size="xs" style={{ marginRight: '5px' }} />
                        {businessInfo.phonenumber}
                    </div>
                )}
            </div>
        </div>
    );
};

// ==============================================
// Main Sell Component
// ==============================================
const Sell = ({ saleId = null, onSuccess = null }) => {
    const { user, currentShop } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) {
            return user.shop_id;
        }
        return localStorage.getItem('current_shop_id') || '';
    });

    const [shopInfo, setShopInfo] = useState({
        id: shopId,
        name: currentShop?.shop_name || localStorage.getItem('current_shop_name') || ''
    });

    const [businessInfo, setBusinessInfo] = useState(null);

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        bill_number: '',
        bill_number_int: 0,
        customer_name: '',
        customer_phone: '',
        customer_address: '',
        customer_id: null,
        subtotal: '0.00',
        tax: '0.00',
        discount: '0.00',
        grand_total: '0.00',
        paid_amount: '0.00',
        balance: '0.00',
        payment_method: 'cash',
        payment_status: 'unpaid',
        notes: '',
        items: [],
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [todaySummary, setTodaySummary] = useState({
        total_sales: 0,
        total_amount: 0,
        total_paid: 0,
        total_balance: 0
    });

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);

    // Customer search states
    const [customerSearchResults, setCustomerSearchResults] = useState([]);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [searchingCustomer, setSearchingCustomer] = useState(false);
    const [newCustomerInfo, setNewCustomerInfo] = useState(null);
    const [showCustomerModal, setShowCustomerModal] = useState(false);

    // Product search states
    const [productSearchTerm, setProductSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [showProductDropdown, setShowProductDropdown] = useState(false);
    const [searchingProducts, setSearchingProducts] = useState(false);

    // Receipt modal state
    const [showReceiptModal, setShowReceiptModal] = useState(false);
    const [currentSale, setCurrentSale] = useState(null);

    // Refs
    const toastTimerRef = useRef(null);
    const customerSearchRef = useRef(null);
    const productSearchRef = useRef(null);
    const searchTimeoutRef = useRef(null);

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        direction: 'ltr',
        progress: 100,
        timeLeft: 3
    });

    // Show toast function
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }

        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';

        setToast({
            show: true,
            type,
            message,
            direction,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;

            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, [currentLanguage]);

    // Clean up timer
    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // Load translations
    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;

        setIsTranslating(true);
        try {
            const trans = await sellTranslator.getTranslations(lang);
            setTranslations(trans);

            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';

        } catch (error) {
            console.error('Error loading translations:', error);
        } finally {
            setIsTranslating(false);
        }
    }, []);

    // Setup language change listener
    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) {
                setCurrentLanguage(newLang);
                loadTranslations(newLang);
            }
        };

        window.addEventListener('storage', handleLanguageChange);

        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 500);

        const translationListener = (lang) => {
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        sellTranslator.addListener(translationListener);

        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            sellTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    // Load business info
    const loadBusinessInfo = useCallback(async () => {
        if (!user?.username) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/get_business_info.php', {
                // const response = await fetch('http://localhost/oz_backend/get_business_info.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_business_info',
                    username: user.username
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setBusinessInfo(data.data);
            }
        } catch (error) {
            console.error('Error loading business info:', error);
        }
    }, [user?.username]);

    // Update shopInfo when currentShop changes
    useEffect(() => {
        if (currentShop) {
            setShopInfo({
                id: currentShop.shop_id,
                name: currentShop.shop_name
            });
        }
    }, [currentShop]);

    // Apply theme
    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';

                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);

                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };

                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };

                Object.entries(palette).forEach(([key, value]) => {
                    document.documentElement.style.setProperty(`--${key}`, value);
                });

            } catch (err) {
                console.error("Error applying theme:", err);
            }
        };

        applyTheme(formData.themeColor);

        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) {
                setFormData(prev => ({ ...prev, themeColor: theme }));
                applyTheme(theme);
            }
        };

        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);

        return () => {
            window.removeEventListener('storage', handleThemeChange);
            clearInterval(interval);
        };
    }, [formData.themeColor]);

    // Get next bill number on mount and when shop changes
    useEffect(() => {
        if (shopId && !saleId) {
            getNextBillNumber();
        }
        loadBusinessInfo();
    }, [shopId, saleId, loadBusinessInfo]);

    // Load sale data if in edit mode
    useEffect(() => {
        if (saleId) {
            setIsEditMode(true);
            loadSaleData(saleId);
        }
    }, [saleId]);

    // Load today's summary
    useEffect(() => {
        if (shopId && user?.username) {
            loadTodaySummary();
        }
    }, [shopId, user?.username]);

    // Calculate totals when items change
    useEffect(() => {
        calculateTotals();
    }, [formData.items, formData.tax, formData.discount]);

    // Click outside handler for customer dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (customerSearchRef.current && !customerSearchRef.current.contains(event.target)) {
                setShowCustomerDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Click outside handler for product dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (productSearchRef.current && !productSearchRef.current.contains(event.target)) {
                setShowProductDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getNextBillNumber = async () => {
        if (!shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_next_bill_number',
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setFormData(prev => ({
                    ...prev,
                    bill_number: data.data.bill_number,
                    bill_number_int: data.data.bill_number_int
                }));
            }
        } catch (error) {
            console.error('Error getting next bill number:', error);
        }
    };

    const loadTodaySummary = async () => {
        if (!user?.username || !shopId) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_today_summary',
                    username: user.username,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success') {
                setTodaySummary(data.data);
            }
        } catch (error) {
            console.error('Error loading today summary:', error);
        }
    };

    const loadSaleData = async (id) => {
        if (!user?.username || !shopId) return;

        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_record',
                    id: id,
                    username: user.username,
                    shop_id: shopId
                })
            });

            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const sale = data.data;
                setFormData(prev => ({
                    ...prev,
                    date: sale.date || new Date().toISOString().split('T')[0],
                    bill_number: sale.bill_number || '',
                    bill_number_int: sale.bill_number_int || 0,
                    customer_id: sale.customer_id || null,
                    customer_name: sale.customer_name || '',
                    customer_phone: sale.customer_phone || '',
                    customer_address: sale.customer_address || '',
                    subtotal: sale.subtotal || '0.00',
                    tax: sale.tax || '0.00',
                    discount: sale.discount || '0.00',
                    grand_total: sale.grand_total || '0.00',
                    paid_amount: sale.paid_amount || '0.00',
                    balance: sale.balance || '0.00',
                    payment_method: sale.payment_method || 'other',
                    payment_status: sale.payment_status || 'unpaid',
                    notes: sale.notes || '',
                    items: sale.items || []
                }));
            } else {
                showToast('error', data.message || 'Failed to load sale data');
            }
        } catch (error) {
            showToast('error', translations?.networkError || 'Network error occurred');
        } finally {
            setLoading(false);
        }
    };

    // Optimized debounce function
    function debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    // Optimized customer search with abort controller
    const searchCustomers = useCallback(
        debounce(async (searchTerm) => {
            if (!searchTerm || searchTerm.length < 2 || !shopId) {
                setCustomerSearchResults([]);
                setShowCustomerDropdown(false);
                return;
            }

            setSearchingCustomer(true);

            // Clear any existing timeout
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }

            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/sell.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'search_customer',
                        search_term: searchTerm,
                        shop_id: shopId
                    })
                });

                const data = await response.json();
                if (data.status === 'success') {
                    setCustomerSearchResults(data.data || []);
                    setShowCustomerDropdown(data.data && data.data.length > 0);
                } else {
                    setCustomerSearchResults([]);
                    setShowCustomerDropdown(false);
                }
            } catch (error) {
                console.error('Error searching customers:', error);
                setCustomerSearchResults([]);
                setShowCustomerDropdown(false);
            } finally {
                setSearchingCustomer(false);
            }
        }, 250), // Reduced debounce time for faster response
        [shopId]
    );

    // Optimized product search
    // Optimized product search - Backend already filters for Saleable products
    const searchProducts = useCallback(
        debounce(async (searchTerm) => {
            if (!searchTerm || searchTerm.length < 2 || !user?.username || !shopId) {
                setFilteredProducts([]);
                setShowProductDropdown(false);
                return;
            }
            setSearchingProducts(true);
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/products.php', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        action: 'search_products',
                        searchTerm: searchTerm,
                        username: user.username,
                        shop_id: shopId
                    })
                });
                const data = await response.json();
                if (data.status === 'success') {
                    // Filter to ensure only Saleable products are shown (extra safety)
                    const saleableProducts = (data.data || []).filter(product =>
                        product.stock_category === 'Saleable' && product.quantity > 0
                    );
                    setFilteredProducts(saleableProducts);
                    setShowProductDropdown(saleableProducts.length > 0);
                } else {
                    setFilteredProducts([]);
                    setShowProductDropdown(false);
                }
            } catch (error) {
                console.error('Error searching products:', error);
                setFilteredProducts([]);
                setShowProductDropdown(false);
            } finally {
                setSearchingProducts(false);
            }
        }, 250),
        [user?.username, shopId]
    );

    const selectCustomer = (customer) => {
        setFormData(prev => ({
            ...prev,
            customer_id: customer.customerid,
            customer_name: customer.customer_name,
            customer_phone: customer.phone || '',
            customer_address: customer.address || ''
        }));
        setShowCustomerDropdown(false);
        setCustomerSearchResults([]);

        showToast('success', `${translations?.customerSelected || 'Customer selected'}: ${customer.customer_name}`);
    };

    const handleProductSelect = (product) => {
        const existingItemIndex = formData.items.findIndex(item => item.product_id === product.id);

        if (existingItemIndex >= 0) {
            const updatedItems = [...formData.items];
            const currentQty = parseFloat(updatedItems[existingItemIndex].quantity) || 0;
            const maxQty = parseFloat(product.quantity);

            if (currentQty + 1 <= maxQty) {
                updatedItems[existingItemIndex].quantity = (currentQty + 1).toString();
                setFormData(prev => ({ ...prev, items: updatedItems }));
                showToast('success', `Added another ${product.product_name}`);
            } else {
                showToast('error', `Cannot add more than available stock (${maxQty} ${product.unit})`);
            }
        } else {
            const newItem = {
                product_id: product.id,
                product_name: product.product_name,
                batch_no: product.batch_no,
                barcode: product.barcode,
                unit: product.unit,
                price: product.price,
                quantity: '1',
                total: product.price,
                available_stock: product.quantity,
                quantity_alert: product.quantity_alert
            };

            setFormData(prev => ({
                ...prev,
                items: [...prev.items, newItem]
            }));

            showToast('success', `${product.product_name} added to cart`);
        }

        setProductSearchTerm('');
        setShowProductDropdown(false);
    };

    const handleItemChange = (index, field, value) => {
        const updatedItems = [...formData.items];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: value
        };

        if (field === 'quantity') {
            const qty = parseFloat(value) || 0;
            const maxQty = parseFloat(updatedItems[index].available_stock) || 0;

            if (qty > maxQty) {
                setErrors(prev => ({
                    ...prev,
                    [`quantity_${index}`]: translations?.quantityExceedsStock || 'Quantity exceeds available stock'
                }));
            } else {
                setErrors(prev => {
                    const newErrors = { ...prev };
                    delete newErrors[`quantity_${index}`];
                    return newErrors;
                });
            }
        }

        setFormData(prev => ({
            ...prev,
            items: updatedItems
        }));

        if (errors[`${field}_${index}`]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`${field}_${index}`];
                return newErrors;
            });
        }
    };

    const addItem = () => {
        if (productSearchRef.current) {
            const input = productSearchRef.current.querySelector('input');
            if (input) input.focus();
        }
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const updatedItems = [...formData.items];
            updatedItems.splice(index, 1);
            setFormData(prev => ({
                ...prev,
                items: updatedItems
            }));

            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[`quantity_${index}`];
                return newErrors;
            });
        }
    };

    const calculateTotals = () => {
        let subtotal = 0;

        formData.items.forEach(item => {
            const price = parseFloat(item.price) || 0;
            const quantity = parseFloat(item.quantity) || 0;
            subtotal += price * quantity;
        });

        const tax = parseFloat(formData.tax) || 0;
        const discount = parseFloat(formData.discount) || 0;
        const grandTotal = subtotal + tax - discount;
        const paid = parseFloat(formData.paid_amount) || 0;
        const balance = grandTotal - paid;
        const paymentStatus = balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid');

        setFormData(prev => ({
            ...prev,
            subtotal: subtotal.toFixed(2),
            grand_total: grandTotal.toFixed(2),
            balance: balance.toFixed(2),
            payment_status: paymentStatus
        }));
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                const qty = parseFloat(item.quantity) || 0;
                const maxQty = parseFloat(item.available_stock) || 0;

                if (qty > maxQty) {
                    newErrors[`quantity_${index}`] = translations?.quantityExceedsStock || 'Quantity exceeds available stock';
                }

                if (qty <= 0) {
                    newErrors[`quantity_${index}`] = translations?.invalidQuantity || 'Please enter a valid quantity';
                }

                const price = parseFloat(item.price) || 0;
                if (price <= 0) {
                    newErrors[`price_${index}`] = translations?.invalidPrice || 'Please enter a valid price';
                }
            });
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'customer_name') {
            searchCustomers(value);
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handlePrintFromModal = () => {
        setShowReceiptModal(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!shopId) {
            showToast('error', translations?.noShopSelected || 'No shop selected. Please select a shop first.');
            return;
        }

        if (!validateForm()) {
            const firstError = document.querySelector('.has-error, .error-message-modern');
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        setSaving(true);

        try {
            const currentUsername = user?.username || '';
            if (!currentUsername) {
                throw new Error("User not authenticated");
            }

            const action = isEditMode ? 'edit' : 'add';
            const endpoint = 'https://ourzestapp.online/oz_backend/sell.php';

            const payload = {
                action: action,
                username: currentUsername,
                shop_id: shopId,
                ...formData,
                items: formData.items.map(item => ({
                    product_id: item.product_id,
                    product_name: item.product_name,
                    batch_no: item.batch_no,
                    barcode: item.barcode,
                    unit: item.unit,
                    quantity: parseFloat(item.quantity),
                    price: parseFloat(item.price),
                    total: parseFloat(item.price) * parseFloat(item.quantity)
                }))
            };

            if (isEditMode && saleId) {
                payload.id = saleId;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.status === 'success') {
                const successMessage = isEditMode
                    ? translations?.saleUpdated || 'Sale updated successfully!'
                    : translations?.saleCompleted || 'Sale completed successfully!';

                showToast('success', successMessage);

                // Set current sale for receipt modal
                const saleForReceipt = {
                    ...data.data,
                    items: data.data.items || formData.items.map(item => ({
                        ...item,
                        total: parseFloat(item.price) * parseFloat(item.quantity)
                    })),
                    subtotal: formData.subtotal,
                    tax: formData.tax,
                    discount: formData.discount,
                    grand_total: formData.grand_total,
                    paid_amount: formData.paid_amount,
                    balance: formData.balance,
                    payment_method: formData.payment_method,
                    payment_status: formData.payment_status,
                    customer_name: formData.customer_name,
                    customer_phone: formData.customer_phone,
                    customer_address: formData.customer_address,
                    username: currentUsername
                };

                setCurrentSale(saleForReceipt);

                // Show customer login info if new customer was created
                if (!isEditMode && data.data?.customer?.is_new && data.data?.customer?.customer_username) {
                    setNewCustomerInfo({
                        name: data.data.customer.name,
                        customer_username: data.data.customer.customer_username,
                        phone: data.data.customer.phone
                    });
                    setShowCustomerModal(true);
                }

                if (!isEditMode) {
                    setFormData(prev => ({
                        ...prev,
                        date: new Date().toISOString().split('T')[0],
                        bill_number: data.data?.bill_number || '',
                        bill_number_int: data.data?.bill_number_int || 0,
                        customer_id: null,
                        customer_name: '',
                        customer_phone: '',
                        customer_address: '',
                        subtotal: '0.00',
                        tax: '0.00',
                        discount: '0.00',
                        grand_total: '0.00',
                        paid_amount: '0.00',
                        balance: '0.00',
                        payment_method: 'other',
                        payment_status: 'unpaid',
                        notes: '',
                        items: []
                    }));

                    getNextBillNumber();
                    loadTodaySummary();
                }

                setErrors({});

                if (onSuccess) {
                    onSuccess(data.data?.sell_id || saleId);
                }

                // Show receipt modal after successful sale
                setTimeout(() => {
                    setShowReceiptModal(true);
                }, 500);

            } else {
                showToast('error', data.message || (isEditMode ? translations?.failedUpdate : translations?.failedSave) || 'Failed to save sale');
            }
        } catch (error) {
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        setFormData(prev => ({
            ...prev,
            date: new Date().toISOString().split('T')[0],
            bill_number: '',
            bill_number_int: 0,
            customer_id: null,
            customer_name: '',
            customer_phone: '',
            customer_address: '',
            subtotal: '0.00',
            tax: '0.00',
            discount: '0.00',
            grand_total: '0.00',
            paid_amount: '0.00',
            balance: '0.00',
            payment_method: 'other',
            payment_status: 'unpaid',
            notes: '',
            items: []
        }));
        setProductSearchTerm('');
        setErrors({});
        setIsEditMode(false);
        getNextBillNumber();
    };

    const handlePaidAmountChange = (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, paid_amount: value }));

        const paid = parseFloat(value) || 0;
        const grandTotal = parseFloat(formData.grand_total) || 0;
        const balance = grandTotal - paid;

        setFormData(prev => ({
            ...prev,
            balance: balance.toFixed(2),
            payment_status: balance <= 0 ? 'paid' : (paid > 0 ? 'partial' : 'unpaid')
        }));
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0.00';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            {/* Toast Notification */}
            <ToastNotification
                toast={toast}
                setToast={setToast}
                translations={translations}
            />

            {/* Customer Login Modal */}
            <CustomerLoginModal
                // show={showCustomerModal}
                show={false}
                customer={newCustomerInfo}
                onClose={() => setShowCustomerModal(false)}
                translations={translations}
                isRTL={isRTL}
            />

            {/* Receipt Modal */}
            <ReceiptModal
                show={showReceiptModal}
                sale={currentSale}
                businessInfo={businessInfo}
                shopInfo={shopInfo}
                translations={translations}
                onPrint={handlePrintFromModal}
                onClose={() => setShowReceiptModal(false)}
            />

            {/* Modern Form Header */}
            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={isEditMode ? faShoppingCart : faShoppingCart} />
                    </div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations?.editSale || 'Edit Sale') : (translations?.newSale || 'New Sale')}</h1>
                        <p>{translations?.sellProducts || 'Sell Products'}</p>
                        {/* {shopInfo.name && (
                                <p className="shop-info">
                                    <FontAwesomeIcon icon={faStore} style={{ marginRight: '5px' }} />
                                    {shopInfo.name}
                                </p>
                            )} */}
                    </div>
                </div>
                {isTranslating && (
                    <div className="translation-indicator">
                        <FontAwesomeIcon icon={faSpinner} spin />
                        <span>Translating...</span>
                    </div>
                )}
            </div>

            {/* Business Info */}
            <BusinessInfo businessInfo={businessInfo} translations={translations} />

            {/* Modern Form */}
            <form onSubmit={handleSubmit} className="modern-form">
                {/* Bill Information Section */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faFileInvoice} />
                            {translations?.saleDetails || 'Sale Information'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faFileInvoice} className="input-icon" />
                                <input
                                    type="text"
                                    id="bill_number"
                                    value={formData.bill_number}
                                    readOnly
                                    className="readonly-input"
                                    dir="ltr"
                                    placeholder=" "
                                />
                                <label htmlFor="bill_number" className="input-label">
                                    {translations?.billNumber || 'Bill Number'}
                                </label>
                            </div>
                        </div>

                        <div className={`form-group-modern ${errors.date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input
                                    type="date"
                                    id="date"
                                    name="date"
                                    value={formData.date}
                                    onChange={handleChange}
                                    disabled={saving}
                                    dir="ltr"
                                    placeholder=" "
                                />
                                <label htmlFor="date" className="input-label">
                                    {translations?.date || 'Date'}
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customer Information Section with Search */}
                <div className="form-section">
                    <div className="section-header">
                        <h3 className="section-title">
                            <FontAwesomeIcon icon={faUser} />
                            {translations?.customerDetails || 'Customer Details'}
                        </h3>
                    </div>

                    <div className="form-grid-modern">
                        <div className="form-group-modern customer-search-group" ref={customerSearchRef}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faUser} className="input-icon" />
                                <input
                                    type="text"
                                    id="customer_name"
                                    name="customer_name"
                                    value={formData.customer_name}
                                    onChange={handleChange}
                                    onFocus={() => formData.customer_name.length >= 2 && setShowCustomerDropdown(true)}
                                    placeholder=" "
                                    disabled={saving}
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                    autoComplete="off"
                                />
                                <label htmlFor="customer_name" className="input-label">
                                    {translations?.customerName || 'Customer Name'}
                                </label>
                                {searchingCustomer && (
                                    <div className="search-indicator">
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                    </div>
                                )}
                            </div>

                            {showCustomerDropdown && customerSearchResults.length > 0 && (
                                <div className="customer-search-dropdown">
                                    {customerSearchResults.map(customer => (
                                        <div
                                            key={customer.customerid}
                                            className="customer-search-item"
                                            onClick={() => selectCustomer(customer)}
                                        >
                                            <div className="customer-name">{customer.customer_name}</div>
                                            {customer.phone && (
                                                <div className="customer-phone">
                                                    <FontAwesomeIcon icon={faPhone} size="xs" />
                                                    {customer.phone}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="form-group-modern">
                            <div className="input-container">
                                <FontAwesomeIcon icon={faPhone} className="input-icon" />
                                <input
                                    type="tel"
                                    id="customer_phone"
                                    name="customer_phone"
                                    value={formData.customer_phone}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    dir="ltr"
                                />
                                <label htmlFor="customer_phone" className="input-label">
                                    {translations?.customerPhone || 'Phone'}
                                </label>
                            </div>
                        </div>

                        {/* <div className="form-group-modern full-width">
                                <div className="input-container">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="input-icon" />
                                    <input
                                        type="text"
                                        id="customer_address"
                                        name="customer_address"
                                        value={formData.customer_address}
                                        onChange={handleChange}
                                        placeholder=" "
                                        disabled={saving}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                    />
                                    <label htmlFor="customer_address" className="input-label">
                                        {translations?.customerAddress || 'Address'}
                                    </label>
                                </div>
                            </div> */}
                    </div>
                </div>

                {/* Product Search Section */}
                <div className="form-section">
                    <div className="form-group-modern product-search-group" ref={productSearchRef}>
                        <div className="input-container">
                            <FontAwesomeIcon icon={faSearch} className="input-icon search-icon" />
                            <input
                                type="text"
                                id="product_search"
                                value={productSearchTerm}
                                onChange={(e) => {
                                    setProductSearchTerm(e.target.value);
                                    searchProducts(e.target.value);
                                }}
                                onFocus={() => productSearchTerm.length >= 2 && setShowProductDropdown(true)}
                                placeholder=" "
                                disabled={saving}
                                dir={isRTL ? 'rtl' : 'ltr'}
                                className="product-search-input"
                                autoComplete="off"
                            />
                            <label htmlFor="product_search" className="input-label">
                                {translations?.searchProducts || 'Search Products'}
                            </label>
                            {searchingProducts && (
                                <div className="search-indicator">
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                </div>
                            )}
                            <button
                                type="button"
                                className="scan-barcode-btn generate-btn"
                                title={translations?.scanBarcode || 'Scan Barcode'}
                            >
                                <FontAwesomeIcon icon={faQrcode} />
                            </button>
                        </div>

                        {showProductDropdown && filteredProducts.length > 0 && (
                            <div className="product-search-dropdown">
                                {filteredProducts
                                    .filter(product => product.stock_category === 'Saleable') // Extra safety filter
                                    .map(product => (
                                        <div
                                            key={product.id}
                                            className="product-search-item"
                                            onClick={() => handleProductSelect(product)}
                                        >
                                            <div className="product-item-name">{product.product_name}</div>
                                            <div className="product-item-details">
                                                {product.batch_no && (
                                                    <span><FontAwesomeIcon icon={faTag} /> {product.batch_no}</span>
                                                )}
                                                {product.barcode && (
                                                    <span><FontAwesomeIcon icon={faBarcode} /> {product.barcode}</span>
                                                )}
                                            </div>
                                            <div className="product-item-price-stock">
                                                <span className="product-price">
                                                    <FontAwesomeIcon icon={faRupeeSign} /> {product.price}
                                                </span>
                                                <span className={`product-stock ${parseFloat(product.quantity) <= (product.quantity_alert || 5) ? 'low-stock' : ''}`}>
                                                    {translations?.stock || 'Stock'}: {product.quantity} {product.unit}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}

                        {showProductDropdown && productSearchTerm.length >= 2 && filteredProducts.length === 0 && !searchingProducts && (
                            <div className="product-search-dropdown empty">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                <span>{translations?.noProductsFound || 'No products found'}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Items Section */}
                {formData.items.length > 0 && (
                    <div className="form-section">
                        <ItemsTable
                            items={formData.items}
                            onItemChange={handleItemChange}
                            onRemove={removeItem}
                            onAdd={addItem}
                            errors={errors}
                            disabled={saving}
                            translations={translations}
                            isRTL={isRTL}
                        />
                    </div>
                )}

                {/* Calculations Section */}
                {formData.items.length > 0 && (
                    <div className="form-section">
                        <div className="section-header">
                            <h3 className="section-title">
                                <FontAwesomeIcon icon={faCalculator} />
                                {translations?.calculations || 'Calculations'}
                            </h3>
                        </div>

                        <div className="calculations-grid">
                            {/* <div className="calculation-row">
                                <span className="calculation-label">{translations?.subtotal || 'Subtotal'}:</span>
                                <span className="calculation-value">Rs. {formData.subtotal}</span>
                            </div> */}

                            {/* <div className="calculation-row">
                                <span className="calculation-label">{translations?.tax || 'Tax'}:</span>
                                <div className="tax-input-wrapper" style={{ width: '120px' }}>
                                    <input
                                        type="number"
                                        id="tax"
                                        name="tax"
                                        value={formData.tax}
                                        onChange={handleChange}
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        disabled={saving}
                                        dir="ltr"
                                        style={{ textAlign: 'right' }}
                                    />
                                </div>
                            </div> */}

                            {/* <div className="calculation-row">
                                <span className="calculation-label">{translations?.discount || 'Discount'}:</span>
                                <div className="discount-input-wrapper" style={{ width: '120px' }}>
                                    <input
                                        type="number"
                                        id="discount"
                                        name="discount"
                                        value={formData.discount}
                                        onChange={handleChange}
                                        step="0.01"
                                        min="0"
                                        placeholder="0.00"
                                        disabled={saving}
                                        dir="ltr"
                                        style={{ textAlign: 'right' }}
                                    />
                                </div>
                            </div> */}

                            <div className="calculation-row grand-total">
                                <span className="calculation-label">{translations?.grandTotal || 'Grand Total'}:</span>
                                <span className="calculation-value highlight">Rs. {formData.grand_total}</span>
                            </div>

                            {/* <div className="calculation-row">
                                <span className="calculation-label">{translations?.paymentMethod || 'Payment Method'}:</span>
                                <div className="payment-method-selector" style={{ width: '120px' }}>
                                    <select
                                        value={formData.payment_method}
                                        onChange={(e) => setFormData(prev => ({ ...prev, payment_method: e.target.value }))}
                                        disabled={saving}
                                        dir={isRTL ? 'rtl' : 'ltr'}
                                        style={{ width: '100%' }}
                                    >
                                        <option value="other">{translations?.other || 'Other'}</option>
                                        <option value="cash">{translations?.cash || 'Cash'}</option>
                                        <option value="card">{translations?.card || 'Card'}</option>
                                        <option value="upi">{translations?.upi || 'UPI'}</option>
                                        <option value="bank_transfer">{translations?.bankTransfer || 'Bank Transfer'}</option>
                                        <option value="credit">{translations?.credit || 'Credit'}</option>
                                    </select>
                                </div>
                            </div> */}

                            <div className={`calculation-row paid-field ${errors.paid ? 'has-error' : ''}`}>
                                <span className="calculation-label">{translations?.paidAmount || 'Paid Amount'}:</span>
                                <div className="paid-input-wrapper" style={{ width: '120px' }}>
                                    <input
                                        type="number"
                                        id="paid_amount"
                                        name="paid_amount"
                                        value={formData.paid_amount}
                                        onChange={handlePaidAmountChange}
                                        step="0.01"
                                        min="0"
                                        max={formData.grand_total}
                                        placeholder="0.00"
                                        disabled={saving}
                                        dir="ltr"
                                        style={{ textAlign: 'right' }}
                                    />
                                </div>
                            </div>

                            {/* <div className="calculation-row balance">
                                <span className="calculation-label">{translations?.balance || 'Balance'}:</span>
                                <span className={`calculation-value ${parseFloat(formData.balance) > 0 ? 'negative' : 'positive'}`}>
                                    Rs. {formData.balance}
                                </span>
                            </div>

                            <div className="calculation-row">
                                <span className="calculation-label">{translations?.paymentStatus || 'Status'}:</span>
                                <span className={`status-badge ${formData.payment_status}`}>
                                    {translations?.[formData.payment_status] || formData.payment_status}
                                </span>
                            </div> */}
                        </div>
                    </div>
                )}

                {/* Notes Section */}
                {/* {formData.items.length > 0 && (
                    <div className="form-section">
                        <div className="form-group-modern">
                            <div className="input-container textarea-container">
                                <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                                <textarea
                                    id="notes"
                                    name="notes"
                                    value={formData.notes}
                                    onChange={handleChange}
                                    placeholder=" "
                                    disabled={saving}
                                    rows="3"
                                    dir={isRTL ? 'rtl' : 'ltr'}
                                ></textarea>
                                <label htmlFor="notes" className="input-label">
                                    {translations?.notes || 'Notes'}
                                </label>
                            </div>
                        </div>
                    </div>
                )} */}

                {/* Form Actions */}
                <div className="form-actions-modern">
                    <button
                        type="button"
                        className="btn-modern btn-secondary-modern"
                        onClick={handleClear}
                        disabled={saving}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                        <span>{translations?.clear || 'Clear'}</span>
                    </button>
                    <button
                        type="submit"
                        className="btn-modern btn-primary-modern"
                        disabled={saving || isTranslating || formData.items.length === 0}
                    >
                        {saving ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>{translations?.saving || 'Saving...'}</span>
                            </>
                        ) : (
                            <>
                                <span>{isEditMode ? (translations?.updateSale || 'Update') : (translations?.completeSale || 'Complete Sale')}</span>
                                <FontAwesomeIcon icon={faSave} />
                            </>
                        )}
                    </button>
                </div>
            </form>

            {/* Loading Overlay */}
            {loading && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>{translations?.loading || 'Loading...'}</p>
                        <p className="loading-subtext">Please wait while we load sale data</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Sell;